import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { DataTable } from '@/components/ui/table/data-table';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { useAccountLogsInfiniteQuery } from '@/hooks/useAccountLogsInfiniteQuery';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import { useModalTryIt, useSessionReplayHref } from '@/hooks/useModalTryIt';
import { usePrefetchSessionContent } from '@/hooks/usePrefetchSessionContent';
import { useSessionKeyboardNav } from '@/hooks/useSessionKeyboardNav';
import DateFilter from '@/modules/analytics/components/filters/DateFilter/DateFilter';
import useFilters from '@/modules/analytics/hooks/useFilters';
import { addLastSeen } from '@/modules/analytics/redux/actions';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { popModal } from '@/redux/modals/actions';
import { selectIsTryItModalOpen } from '@/redux/modals/selectors';
import { AnalyticsType, Log, LogsAnalytics } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';

import { AccountFiltersDropdown } from './AccountFiltersDropdown';
import { createAccountSessionColumns } from './accountSessionColumns';
import { EmptySessions } from './EmptySessions';
import { FiltersBar } from './FiltersBar';
import { useAccountFilterViews } from './useAccountFilterViews';
import { AccountFilterFormValue, useAccountSessionFilters } from './useAccountSessionFilters';

// Allowed filters for account chat logs
const ACCOUNT_CHAT_LOGS_ALLOWED_FILTERS = [
  'channels',
  'deskIds',
  'brainIds',
  'agentIds',
  'languageModelIds',
  'tags',
  'isCovered',
  'isContained',
  'isMeaningful',
  'hasCollection',
  'hasRecording',
  'hasVoicemail',
  'isTest',
  'minNumUserMessages',
  'sessionId',
  'userId',
  'userName',
  'userEmail',
  'channelUserId',
  'externalUserId',
  'ratings',
  'csatBrain',
  'goalAchieved',
  'userSentiment',
  'reactions',
  'insightFilters',
  'firstMessageType',
];

/**
 * Account-scoped wrapper around the sessions infinite table.
 * Uses the shared DataTable.InfiniteContent for consistency.
 */

export const AccountSessionsInfiniteTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { languageModelNames } = useLanguageModels();
  const lastSeen = useSelector((state: RootState) => state.analytics.lastSeen);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const sessionHandledRef = useRef<string | null>(null);
  const { handleAccountReplayClick } = useModalTryIt(sessionId ?? undefined, {
    showNavigationHint: true,
  });
  const prefetchSessionContent = usePrefetchSessionContent();

  const {
    data,
    fetchMore,
    hasNextPage,
    totalCount,
    isCountLoading,
    isLoading,
    isFetchingNextPage,
  } = useAccountLogsInfiniteQuery({ limit: 20 });

  const renderSessionCount = () => {
    if (isCountLoading && data.length > 0) {
      return <Skeleton className="h-4 w-28" />;
    }
    if (totalCount === 0) {
      return null;
    }
    return t('analytics.total_sessions', { count: totalCount });
  };

  useEffect(() => {
    if (!sessionId) {
      // Forget the handled session when session_id leaves the URL (e.g. Back), so
      // navigating Forward to the same session re-opens the modal
      sessionHandledRef.current = null;
      return;
    }

    if (sessionHandledRef.current === sessionId) {
      return;
    }

    sessionHandledRef.current = sessionId;
    handleAccountReplayClick(sessionId);
    if (!lastSeen.includes(sessionId)) {
      dispatch(addLastSeen(sessionId));
    }
  }, [sessionId, handleAccountReplayClick, dispatch, lastSeen]);

  useEffect(() => {
    return () => {
      // Forget the handled session on unmount so a remount re-opens the modal.
      // Without this, an unmount/remount while the session is still in the URL
      // (React StrictMode's dev double-invoke, or any real remount) pops the
      // modal here, then the open effect above short-circuits on the stale ref
      // and never re-opens it.
      sessionHandledRef.current = null;
      dispatch(popModal());
    };
  }, [dispatch]);

  const handleReplayClick = useCallback(
    (session: Log) => {
      handleAccountReplayClick(session.session_id, session.brain_parent_id, session.start_time);
      const lastSeenId = lastSeen[lastSeen.length - 1];
      if (session.session_id && lastSeenId !== session.session_id) {
        dispatch(addLastSeen(session.session_id));
      }
    },
    [dispatch, handleAccountReplayClick, lastSeen]
  );

  const isTryItModalOpen = useSelector(selectIsTryItModalOpen);
  useSessionKeyboardNav(data, sessionId, isTryItModalOpen, handleReplayClick, {
    fetchMore,
    hasNextPage,
    isFetchingNextPage,
  });

  // ?session_id= in a new tab auto-opens the replay modal; left click keeps the onRowClick path
  const getSessionReplayHref = useSessionReplayHref();
  const getRowHref = useCallback(
    (session: Log) => getSessionReplayHref(session.session_id),
    [getSessionReplayHref]
  );

  const columns = useMemo(
    () =>
      createAccountSessionColumns(t, handleReplayClick, desks, lastSeen, prefetchSessionContent),
    [t, handleReplayClick, desks, lastSeen, prefetchSessionContent]
  );

  // Column visibility management
  const {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  } = useColumnVisibility(columns, 'account-logs');

  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);

  const isEmptyState = !isLoading && data.length === 0;

  // Button ref will be used to prevent the popover modal from closing when you click out
  const customizeButtonRef = useRef<HTMLButtonElement>(null);

  // New Account filters UI is always enabled; legacy rendering removed

  const {
    formMethods,
    fields,
    addFilter,
    removeFilter,
    saveFilters,
    activeFilters,
    filtersForDisplay,
    removeFilterByType,
    loadFilters,
    clearAllFilters,
  } = useAccountSessionFilters();
  const accountFilters = activeFilters;

  const { dateOpen, setDateOpen, filterOpen } = useFilters(AnalyticsType.ACCOUNT);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const filterIndexByType = useMemo(
    () =>
      fields.reduce(
        (acc, field, index) => {
          const fieldType = (field as AccountFilterFormValue | undefined)?.type;
          if (fieldType) {
            acc[fieldType] = index;
          }
          return acc;
        },
        {} as Record<string, number>
      ),
    [fields]
  );

  const {
    views: savedViews,
    activeView,
    activeViewId,
    hasUnsavedChanges,
    isCheckingMatch,
    saveFilterView,
    loadFilterView,
    deleteFilterView,
    setActiveViewId,
  } = useAccountFilterViews<AccountFilterFormValue>(filtersForDisplay);

  const handleLoadView = useCallback(
    (viewId: string) => {
      const filters = loadFilterView(viewId);
      if (filters) {
        loadFilters(filters);
      }
    },
    [loadFilterView, loadFilters]
  );

  const handleSaveView = useCallback(
    async ({ name, overwriteExisting }: { name: string; overwriteExisting: boolean }) => {
      const overwriteId = overwriteExisting ? activeViewId : undefined;
      await saveFilterView(name, overwriteId);
    },
    [activeViewId, saveFilterView]
  );

  const handleUpdateActiveView = useCallback(async () => {
    if (activeView) {
      await saveFilterView(activeView.name, activeView.id);
    }
  }, [activeView, saveFilterView]);

  const handleDeleteView = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteViewConfirm = useCallback(async () => {
    if (activeView) {
      await deleteFilterView(activeView.id);
      clearAllFilters();
      setActiveViewId(undefined);
    }
    setDeleteConfirmOpen(false);
  }, [activeView, clearAllFilters, deleteFilterView, setActiveViewId]);

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setActiveViewId(undefined);
  }, [clearAllFilters, setActiveViewId]);

  const handleRemoveFilter = useCallback(
    (filterType: string) => {
      if (filterType.startsWith('insightFilter-')) {
        const insightId = filterType.replace('insightFilter-', '');
        const insightFilter = accountFilters.find((filter) => filter.type === 'insightFilters');

        if (insightFilter?.insightFilters) {
          const remaining = insightFilter.insightFilters.filter(
            (insight) => insight.id !== insightId
          );

          if (remaining.length === 0 && accountFilters.length === 1) {
            setActiveViewId(undefined);
          }
        }
      } else if (accountFilters.length === 1) {
        setActiveViewId(undefined);
      }

      removeFilterByType(filterType);
    },
    [accountFilters, removeFilterByType, setActiveViewId]
  );

  return (
    <FormProvider {...formMethods}>
      <div className="flex h-full min-h-0 w-full flex-col">
        <DataTable.Root<Log, unknown>
          fill
          className="min-h-0 flex-1"
          columns={columns}
          data={data}
          resourceName="session"
          fetchNextPage={fetchMore}
          hasNextPage={hasNextPage}
          rowCount={totalCount}
          onRowClick={handleReplayClick}
          getRowHref={getRowHref}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          pagination={true}
        >
          <DataTable.Toolbar>
            {(_table) => (
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DateFilter dateOpen={dateOpen} setDateOpen={setDateOpen} disabled={filterOpen} />
                </div>
                <div className="flex items-center gap-2">
                  <AccountFiltersDropdown
                    formMethods={formMethods}
                    fields={fields}
                    addFilter={addFilter}
                    removeFilter={removeFilter}
                    saveFilters={saveFilters}
                    activeFilters={filtersForDisplay}
                    savedViews={savedViews}
                    onLoadView={handleLoadView}
                    allowedFilters={ACCOUNT_CHAT_LOGS_ALLOWED_FILTERS}
                  />
                  <ColumnVisibilityMenu
                    columns={hideableColumns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    onReset={resetColumnVisibility}
                    open={isCustomizeOpen}
                    onOpenChange={setIsCustomizeOpen}
                    onInteractOutside={(e) => {
                      if (e.target === customizeButtonRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </DataTable.Toolbar>
          <FiltersBar
            filters={filtersForDisplay as unknown as LogsAnalytics[]}
            scope="account"
            onRemoveFilter={handleRemoveFilter}
            desks={desks}
            brains={brains}
            languageModelNames={languageModelNames}
            onSaveView={handleSaveView}
            onUpdateActiveView={handleUpdateActiveView}
            onDeleteView={handleDeleteView}
            onSelectView={handleLoadView}
            activeView={activeView}
            existingViews={savedViews}
            activeViewId={activeViewId}
            hasUnsavedChanges={hasUnsavedChanges}
            isCheckingMatch={isCheckingMatch}
            filterIndexByType={filterIndexByType}
            removeFilterAtIndex={removeFilter}
            onSaveFilters={saveFilters}
            onClearAll={handleClearFilters}
          />
          {isEmptyState ? <EmptySessions variant="short" /> : <DataTable.InfiniteContent />}
        </DataTable.Root>

        {/* Footer: Total count + Hidden columns indicator */}
        {(totalCount > 0 || data.length > 0 || hasHiddenColumns) && (
          <div className="mt-4 grid h-10 grid-cols-3 items-center">
            <div className="flex items-center text-sm text-foreground-muted">
              {renderSessionCount()}
            </div>
            <div className="flex justify-center">
              {hasHiddenColumns && (
                <HiddenColumnsIndicator
                  onCustomize={() => setIsCustomizeOpen((prev) => !prev)}
                  buttonRef={customizeButtonRef}
                />
              )}
            </div>
            <div />
          </div>
        )}

        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('brain_logs.delete_filter_view_title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('brain_logs.delete_filter_view_description', { 0: activeView?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <Button onClick={handleDeleteViewConfirm} variant="destructive">
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </FormProvider>
  );
};
